import React, { FC, useEffect, useState } from 'react';

import { useTheme } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';

import AddToList from '~/assets/icons/AddToList';
import CircleCheckIcon from '~/assets/icons/CircleCheckIcon';
import PlayWithLinesIcon from '~/assets/icons/PlayWithLinesIcon';
import ProgressIcon from '~/assets/icons/ProgressIcon';
import RemoveFromList from '~/assets/icons/RemoveFromList';
import ShareIcon from '~/assets/icons/ShareIcon';
import Duration, { Label } from '~/components/Duration';
import ToggleWatchlistPopper from '~/components/ToggleFromList/ToggleWatchlistPopper';
import IconButton from '~/components/UI/IconButton/IconButton';
import { LightTooltip } from '~/components/UI/LightTooltip/LightTooltip';
import { BodyText, H3 } from '~/components/UI/Texts';
import FollowButton from '~/components/buttons/FollowButton';
import { useAlert } from '~/context/AlertProvider';
import { useWithoutNavigation } from '~/context/OnlineContentConnectorContext';
import { useUserInfoContext } from '~/context/UserInfoProvider';
import useFollowExperts from '~/hooks/useFollowExperts';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import useToggleFromWatchlist from '~/hooks/useToggleFromWatchlist';
import ShareDropdown, { Align } from '~/pages/SingleVideoPage/components/ShareDropdown';
import ThumbBlock from '~/pages/SingleVideoPage/components/ThumbBlock';
import { VIDEO_PAGE_EVENTS } from '~/pages/SingleVideoPage/const';
import { IUseVideoReview } from '~/pages/SingleVideoPage/hooks/useVideoReview';
import { PRICING_ROUTE } from '~/routes';
import { SMALL_MARGIN_PX, SUBMIDDLE_MARGIN_PX, TINY_MARGIN_PX, useIsWidthUp } from '~/theme';
import sendAnalyticEvent from '~/utils/sendAnalyticEvent';

interface VideoTitleProps {
  title?: string;
  isXApiPage?: boolean;
  existsInWatchlist?: boolean;
  videoId: string;
  duration: number;
  rating?: number | null;
  progress?: number | null;
  isCompleted?: boolean | null;
  availableForFree?: boolean;
  videoReview?: IUseVideoReview;
  handleOpenComments?: () => void;
  expertId?: string;
}

const VideoTitle: FC<VideoTitleProps> = ({
  title,
  isXApiPage,
  existsInWatchlist = false,
  videoId,
  duration,
  rating,
  progress,
  isCompleted,
  availableForFree,
  videoReview,
  handleOpenComments,
  expertId,
}) => {
  const theme = useTheme();
  const { t } = useTenantTranslation();
  const [existsInList, setExistsInList] = useState<boolean>(existsInWatchlist);
  const { userInfo, hasAccessToContent } = useUserInfoContext();
  const { showAlert, hideAlert } = useAlert();
  const isMobile = !useIsWidthUp('md');
  const withoutNavigation = useWithoutNavigation();
  const {
    followed,
    toggleFollowing,
    loading: loadingToggleFollowing,
  } = useFollowExperts([expertId]);
  const { toggleWatchlist, loading } = useToggleFromWatchlist(
    Boolean(existsInList),
    String(videoId),
  );

  const handleToggleWatchlist = async (event) => {
    const ref = event.currentTarget;
    await toggleWatchlist();
    if (existsInList) {
      setExistsInList(false);
    } else {
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.addToWatchlist);
      setExistsInList(true);
    }
    showAlert({
      message: <ToggleWatchlistPopper existInWatchList={existsInList} />,
      ref,
    });
  };

  const handleClickFollow = () => {
    if (!followed) {
      sendAnalyticEvent(VIDEO_PAGE_EVENTS.follow);
    }
    toggleFollowing();
  };

  useEffect(() => {
    setExistsInList(existsInWatchlist);
  }, [existsInWatchlist]);

  useEffect(() => {
    return () => {
      hideAlert();
    };
  }, [hideAlert]);

  return (
    <div>
      <TitleRow data-testid="videoTitleControls">
        <Title variant="h3" data-testid="videoPlayerTitle">
          {title}
        </Title>
        <Row>
          {!withoutNavigation && !isXApiPage && hasAccessToContent && !isMobile && (
            <ThumbBlock
              rating={rating}
              thumbSelected={videoReview?.thumbSelected}
              likedVideo={videoReview?.likedVideo}
              leaveReview={videoReview?.leaveReview}
              setLikedOutsideCommentSection={videoReview?.setLikedOutsideCommentSection}
              handleOpenComments={handleOpenComments}
            />
          )}
          {!withoutNavigation && !isXApiPage && !isMobile && (
            <ButtonsWrapper id="videoButtons">
              {!isXApiPage && !!userInfo && (
                <FollowButton
                  loading={loadingToggleFollowing}
                  followed={followed}
                  onClick={handleClickFollow}
                />
              )}
              <StyledShareDropdown>
                <IconButton
                  icon={<StyledShareIcon color={theme.palette.common.primary} />}
                  animationName="ShareIcon"
                  size="large"
                  label={t('button.share')}
                />
              </StyledShareDropdown>
              {!hasAccessToContent && availableForFree && (
                <StyledLink to={PRICING_ROUTE}>
                  <IconButton
                    icon={<StyledPlayWithLinesIcon color={theme.palette.common.primary} />}
                    size="large"
                    label={t('button.startFreeTrial')}
                  />
                </StyledLink>
              )}
              {hasAccessToContent && (
                <LightTooltip
                  title={t(existsInList ? 'button.removeFromWatchlist' : 'button.addToWatchlist')}
                  placement="bottom"
                >
                  <StyledIconButton
                    animationName={existsInList ? 'RemoveFromList' : 'AddToList'}
                    disabled={loading}
                    aria-label={t(
                      existsInList ? 'button.removeFromWatchlist' : 'button.addToWatchlist',
                    )}
                    icon={existsInList ? <StyledRemoveFromListIcon /> : <StyledAddToListIcon />}
                    data-testid={existsInList ? 'removeFromWatchlistBtn' : 'addToWatchlistBtn'}
                    label={t(existsInList ? 'button.saved' : 'button.save')}
                    onClick={handleToggleWatchlist}
                    size="large"
                  />
                </LightTooltip>
              )}
            </ButtonsWrapper>
          )}
        </Row>
      </TitleRow>
      <LabelsWrapper data-testid="videoLabels">
        <Duration duration={duration} />
        {hasAccessToContent && Boolean(progress) && (
          <Label
            color={
              isCompleted ? theme.palette.common.green : alpha(theme.palette.common.white, 0.33)
            }
          >
            {isCompleted ? (
              <CircleCheckIcon color={theme.palette.common.green} />
            ) : (
              <ProgressIcon />
            )}
            <BodyText>
              {isCompleted ? (
                t('common.watched')
              ) : (
                <>
                  {Math.round(progress as number)}% <LowerCase>{t('common.watched')}</LowerCase>
                </>
              )}
            </BodyText>
          </Label>
        )}
      </LabelsWrapper>
      <Row>
        {!withoutNavigation && !isXApiPage && hasAccessToContent && isMobile && (
          <ThumbBlock
            rating={rating}
            thumbSelected={videoReview?.thumbSelected}
            likedVideo={videoReview?.likedVideo}
            leaveReview={videoReview?.leaveReview}
            setLikedOutsideCommentSection={videoReview?.setLikedOutsideCommentSection}
            handleOpenComments={handleOpenComments}
          />
        )}
        {isMobile && (
          <ButtonsWrapper id="videoButtons">
            {!isXApiPage && (
              <>
                {!isXApiPage && !!userInfo && (
                  <FollowButton
                    loading={loadingToggleFollowing}
                    followed={followed}
                    onClick={toggleFollowing}
                    onlyIcon
                  />
                )}
                <StyledShareDropdown align={hasAccessToContent ? Align.Right : Align.Left}>
                  {hasAccessToContent ? (
                    <Button>
                      <StyledShareIcon color={theme.palette.common.primary} />
                    </Button>
                  ) : (
                    <IconButton
                      icon={<StyledShareIcon color={theme.palette.common.primary} />}
                      size="large"
                      label={t('button.share')}
                    />
                  )}
                </StyledShareDropdown>
                {!hasAccessToContent && availableForFree && (
                  <PricingLink to={PRICING_ROUTE}>
                    <IconButton
                      icon={<StyledPlayWithLinesIcon color={theme.palette.common.primary} />}
                      size="large"
                      label={t('button.startFreeTrial')}
                    />
                  </PricingLink>
                )}
                {hasAccessToContent && (
                  <AddToWatchlistBtn disabled={loading} onClick={handleToggleWatchlist}>
                    {existsInList ? <StyledRemoveFromListIcon /> : <StyledAddToListIcon />}
                  </AddToWatchlistBtn>
                )}
              </>
            )}
          </ButtonsWrapper>
        )}
      </Row>
    </div>
  );
};

export default VideoTitle;

const Title = styled(H3)`
  &:not(:only-child) {
    margin-right: ${SMALL_MARGIN_PX};
  }
`;
const Row = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    justify-content: flex-end;
    margin-top: 0;
  }
`;
const ButtonsWrapper = styled('div')`
  display: flex;
  align-items: center;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-left: ${SUBMIDDLE_MARGIN_PX};
  }
`;
const LabelsWrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;
const Button = styled('button')`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
`;
const AddToWatchlistBtn = styled(Button)`
  margin-left: ${SUBMIDDLE_MARGIN_PX};
`;
const PricingLink = styled(Link)`
  margin-left: ${SUBMIDDLE_MARGIN_PX};
`;
const LowerCase = styled('span')`
  text-transform: lowercase;
`;
const TitleRow = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${TINY_MARGIN_PX};
`;
const createWatchlistIcon = (component) => styled(component)`
  width: 24px;
  min-width: 24px;
  height: 24px;
`;
const createIconStyles = (component) => styled(component)`
  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 2px;
  }
`;
const StyledShareIcon = createIconStyles(createWatchlistIcon(ShareIcon));
const StyledPlayWithLinesIcon = createIconStyles(createWatchlistIcon(PlayWithLinesIcon));
const StyledAddToListIcon = createWatchlistIcon(AddToList);
const StyledRemoveFromListIcon = createWatchlistIcon(RemoveFromList);
const createStylesItem = (tag) => styled(tag)`
  margin-left: ${SUBMIDDLE_MARGIN_PX};
`;
const StyledLink = createStylesItem(Link);
const StyledIconButton = createStylesItem(IconButton);
const StyledShareDropdown = createStylesItem(ShareDropdown);
